import { AdditionalQuestionsCardContent } from "@/components/license-details/cards/additional-questions-card";
import { PropertyCardContent } from "@/components/license-details/cards/property-card";
import { PartyRoleInfo } from "@/components/license-details/party-role-info";
import {
  QuestionField,
  companyCopyFieldProps,
  extractBracketLabel,
  validatorForQuestionType,
} from "@/components/registrant/form-utils/question-field-utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ErrorComponent from "@/components/ui/error";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Label } from "@/components/ui/label";
import LoadingScreen from "@/components/ui/loading-screen";
import { useAppForm } from "@/components/ui/tanstack-form";
import { Textarea } from "@/components/ui/textarea";
import { UserType } from "@/constants";
import {
  getAdminUsersOptions,
  getLicenseTypeQuestionsOptions,
  getPartiesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { updateInspection } from "@/hera-api/client/sdk.gen";
import type {
  InspectionStatus,
  InspectionWithLicenseResponseSchema,
  LicenseResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import type { PartyResponseSchema } from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useCommentHandlers } from "@/hooks/use-comment-handlers";
import {
  answerOptionsToSelectOptions,
  getFirstAnswerOptionKey,
} from "@/lib/answer-option-order";
import { nacFieldRequirementsForValidation } from "@/lib/contact-question-requirements";
import { refreshLicensesAfterLocalChange } from "@/lib/license-details-change-notification";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import {
  getRegistrantDisplayAddress1,
  getRegistrantDisplayAddress2,
  getRegistrantDisplayCity,
  getRegistrantDisplayCompanyName,
  getRegistrantDisplayName,
  getRegistrantDisplayState,
  getRegistrantDisplayZipcode,
  hasRegistrantDisplayAddress,
} from "@/lib/registrant-display";
import { formatPhoneNumber, getQueryErrorMessage } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

interface PerformInspectionFlowProps {
  inspection: InspectionWithLicenseResponseSchema;
  license: LicenseResponseSchema | null;
  userType: UserType;
  onComplete: () => void;
  onCancel: () => void;
}

type InspectionAnswers = Record<string, any>;

export function PerformInspectionFlow({
  inspection,
  license,
  userType,
  onComplete,
  onCancel,
}: PerformInspectionFlowProps) {
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { user: auth0User } = useAuth0();
  const queryClient = useQueryClient();

  // Get current user from backend
  const { data: usersData } = useQuery({
    ...getAdminUsersOptions({
      query: {
        include_deleted: false,
      },
    }),
    enabled: isAuthSetupComplete && !!auth0User?.email,
  });

  const currentUser = useMemo(() => {
    if (!usersData?.users || !auth0User?.email) return null;
    return usersData.users.find((u) => u.email === auth0User.email) ?? null;
  }, [usersData, auth0User]);

  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [isReassigning, setIsReassigning] = useState(false);
  const [questionComments, setQuestionComments] = useState<
    Record<string, string>
  >({});
  const [questionShareFlags, setQuestionShareFlags] = useState<
    Record<string, boolean>
  >({});
  const [sharedComment, setSharedComment] = useState("");
  const [internalComment, setInternalComment] = useState("");

  // Comment handlers for adding comments to the license
  const commentHandlers = useCommentHandlers({
    licenseId: license?.id ?? inspection.license_id ?? "",
  });

  // Fetch questions for this license's type and jurisdiction (global + jurisdiction + jurisdiction-assigned group questions)
  const licenseTypeId = license?.license_type?.id;
  const jurisdictionId = license?.jurisdiction?.id;
  const {
    data: questionsData,
    isLoading: isQuestionsLoading,
    error: questionsError,
  } = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseTypeId ?? "",
        jurisdiction_id: jurisdictionId ?? "",
      },
      query: {
        include_deleted: false,
      },
    }),
    enabled:
      isAuthSetupComplete && !!license && !!licenseTypeId && !!jurisdictionId,
  });

  // Fetch parties for party tiles
  const { data: partiesData } = useQuery({
    ...getPartiesOptions({
      query: { include_deleted: false },
    }),
    enabled: isAuthSetupComplete && !!license?.id,
  });

  // Filter to only inspection questions (registration/deregistration use same API but filter to their type)
  const inspectionQuestions = useMemo(() => {
    if (!questionsData?.questions) return [];

    return questionsData.questions.filter(
      (q) => q.question_type === "inspection",
    );
  }, [questionsData]);

  // Registration questions for additional-questions tile and party tiles
  const registrationQuestions = useMemo(() => {
    if (!questionsData?.questions) return [];
    return questionsData.questions.filter(
      (q) => q.question_type === "registration",
    );
  }, [questionsData]);

  // Parties referenced in additional questions (name_address_contact type) for read-only tiles
  const partyTilesData = useMemo(() => {
    if (!license?.answers_to_additional_questions || !partiesData?.parties)
      return [];
    const answers = license.answers_to_additional_questions;
    const partiesById = new Map(partiesData.parties.map((p) => [p.id, p]));
    const result: {
      questionDef: QuestionResponseSchema;
      party: PartyResponseSchema;
    }[] = [];
    for (const q of registrationQuestions) {
      if (!isNacStyleAnswerType(q.answer_type)) continue;
      const nacId = answers[q.id];
      if (nacId == null || typeof nacId !== "string" || !nacId.trim()) continue;
      const party = partiesById.get(nacId);
      if (party) result.push({ questionDef: q, party });
    }
    return result;
  }, [
    license?.answers_to_additional_questions,
    partiesData?.parties,
    registrationQuestions,
  ]);

  // Add "Inspection Date" as the first question
  const allQuestions = useMemo(() => {
    const inspectionDateQuestion: QuestionResponseSchema = {
      id: "inspection_date",
      question: "Inspection Date",
      answer_type: "date",
      answer_options: null,
      display_order: 0,
      answer_required: true,
      question_type: "inspection",
    };

    return [inspectionDateQuestion, ...inspectionQuestions];
  }, [inspectionQuestions]);

  // Check if reassignment is needed (only for government users)
  const needsReassignment =
    userType === UserType.GOVERNMENT &&
    inspection.assignee_id !== currentUser?.id &&
    !isReassigning;

  // Show reassignment dialog on mount if needed
  useEffect(() => {
    if (needsReassignment) {
      setShowReassignDialog(true);
    }
  }, [needsReassignment]);

  // Mutation to update inspection
  const updateInspectionMutation = useMutation({
    mutationFn: async (data: {
      status?: InspectionStatus;
      assignee_id?: string;
      effective_date?: string;
      effective_end_date?: string;
      answers_to_additional_questions?: Record<string, any>;
    }) => {
      return await updateInspection({
        path: {
          inspection_id: inspection.id,
        },
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["inspections"],
      });
      queryClient.invalidateQueries({
        queryKey: ["licenseDetails"],
      });
      void refreshLicensesAfterLocalChange(queryClient, inspection.license_id);
    },
    onError: (error) => {
      toast.error("Failed to update inspection", {
        description: getQueryErrorMessage(error),
      });
    },
  });

  // Handle reassignment
  const handleReassign = async (reassign: boolean) => {
    if (reassign) {
      setIsReassigning(true);
      try {
        await updateInspectionMutation.mutateAsync({
          assignee_id: currentUser?.id,
        });
        toast.success("Inspection reassigned to you");
        setShowReassignDialog(false);
      } catch (error) {
        toast.error("Failed to reassign inspection", {
          description: getQueryErrorMessage(error),
        });
      }
    } else {
      setShowReassignDialog(false);
      onCancel();
    }
  };

  // Default values for the form (pre-fill inspection date to today)
  const defaultValues = useMemo(() => {
    const values: InspectionAnswers = {
      inspection_date: format(new Date(), "yyyy-MM-dd"),
    };

    // Pre-fill any existing answers
    if (inspection.answers_to_additional_questions) {
      Object.assign(values, inspection.answers_to_additional_questions);
    }

    for (const question of allQuestions) {
      if (question.id === "inspection_date") continue;
      if (question.answer_type !== "single_select") continue;
      const first = getFirstAnswerOptionKey(
        question.answer_options,
        question.answer_option_keys_order,
      );
      if (first === undefined) continue;
      const current = values[question.id];
      if (current === undefined || current === null || current === "") {
        values[question.id] = first;
      }
    }

    return values;
  }, [inspection.answers_to_additional_questions, allQuestions]);

  const form = useAppForm({
    defaultValues,
    onSubmit: async () => {
      // Form validation handled in handleCompleteInspection
    },
  });

  const handleBack = () => {
    onCancel();
  };

  const handleCompleteInspection = async (passed: boolean) => {
    try {
      const answers = form.state.values;
      const inspectionDate = answers.inspection_date as string;

      const status: InspectionStatus = passed ? "COMPLIANT" : "NON_COMPLIANT";

      // Extract answers_to_additional_questions (exclude inspection_date)
      const { inspection_date: _, ...answersToQuestions } = answers;

      // Update the inspection
      await updateInspectionMutation.mutateAsync({
        status,
        effective_date: inspectionDate,
        answers_to_additional_questions: answersToQuestions,
      });

      // Add comment if provided
      const licenseId = license?.id ?? inspection.license_id;
      if (licenseId) {
        // Add comments for each question that has a comment
        for (const [questionId, commentText] of Object.entries(
          questionComments,
        )) {
          if (commentText.trim()) {
            try {
              await commentHandlers.handleAddComment(
                commentText,
                questionShareFlags[questionId] ?? false,
              );
            } catch (commentError) {
              // Comment error is already handled by handleAddComment (shows toast)
              // Continue with completion even if comment fails
            }
          }
        }

        // Add shared comment if filled
        if (sharedComment.trim()) {
          try {
            await commentHandlers.handleAddComment(sharedComment, true);
          } catch (commentError) {
            // Comment error is already handled by handleAddComment (shows toast)
            // Continue with completion even if comment fails
          }
        }

        // Add internal comment if filled
        if (internalComment.trim()) {
          try {
            await commentHandlers.handleAddComment(internalComment, false);
          } catch (commentError) {
            // Comment error is already handled by handleAddComment (shows toast)
            // Continue with completion even if comment fails
          }
        }
      }

      toast.success(`Inspection marked as ${passed ? "Passed" : "Failed"}`);
      onComplete();
    } catch (error) {
      toast.error("Failed to complete inspection", {
        description: getQueryErrorMessage(error),
      });
    }
  };

  if (isQuestionsLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingScreen message="Loading inspection questions..." />
      </div>
    );
  }

  if (questionsError) {
    return (
      <ErrorComponent
        title="Error loading questions"
        message={getQueryErrorMessage(questionsError)}
      />
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-background z-50 overflow-y-auto">
        <div className="container max-w-2xl mx-auto py-8 px-4 space-y-6">
          {/* Property tile (read-only) */}
          {license?.property && (
            <Card className="gap-4">
              <CardHeader>
                <CardTitle className="font-semibold text-xl">
                  Property
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PropertyCardContent
                  property={license.property}
                  jurisdiction={license.jurisdiction}
                  userType={userType}
                  licenseId={license.id}
                />
              </CardContent>
            </Card>
          )}

          {/* License registrant tile (read-only) */}
          {license?.registrant_account && (
            <Card className="gap-4">
              <CardHeader>
                <CardTitle className="font-semibold text-xl">
                  License Registrant
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {getRegistrantDisplayName(license.registrant_account) && (
                  <div>
                    <p className="text-sm font-bold">Name</p>
                    <p className="text-sm">
                      {getRegistrantDisplayName(license.registrant_account)}
                    </p>
                  </div>
                )}
                {getRegistrantDisplayCompanyName(
                  license.registrant_account,
                ) && (
                  <div>
                    <p className="text-sm font-bold">Company</p>
                    <p className="text-sm">
                      {getRegistrantDisplayCompanyName(
                        license.registrant_account,
                      )}
                    </p>
                  </div>
                )}
                {license.registrant_account.email != null &&
                  license.registrant_account.email !== "" && (
                    <div>
                      <p className="text-sm font-bold">Email</p>
                      <p className="text-sm">
                        {license.registrant_account.email}
                      </p>
                    </div>
                  )}
                {license.registrant_account.phone != null &&
                  license.registrant_account.phone !== "" && (
                    <div>
                      <p className="text-sm font-bold">Phone</p>
                      <p className="text-sm">
                        {formatPhoneNumber(license.registrant_account.phone)}
                      </p>
                    </div>
                  )}
                {hasRegistrantDisplayAddress(license.registrant_account) && (
                  <div>
                    <p className="text-sm font-bold">Address</p>
                    <p className="text-sm">
                      {[
                        getRegistrantDisplayAddress1(
                          license.registrant_account,
                        ),
                        getRegistrantDisplayAddress2(
                          license.registrant_account,
                        ),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      {(getRegistrantDisplayAddress1(
                        license.registrant_account,
                      ) ||
                        getRegistrantDisplayAddress2(
                          license.registrant_account,
                        )) &&
                        (getRegistrantDisplayCity(license.registrant_account) ||
                          getRegistrantDisplayState(
                            license.registrant_account,
                          ) ||
                          getRegistrantDisplayZipcode(
                            license.registrant_account,
                          )) &&
                        " "}
                      {[
                        getRegistrantDisplayCity(license.registrant_account),
                        getRegistrantDisplayState(license.registrant_account),
                        getRegistrantDisplayZipcode(license.registrant_account),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Party tiles (read-only) - name/contact/address questions from license */}
          {partyTilesData.map(({ questionDef, party }) => {
            const useCoLabel =
              questionDef.answer_type === "company_contact" &&
              Boolean(questionDef.use_contact_label);
            const personLabel = useCoLabel ? "Contact" : "Name";
            const nameRow =
              party.name != null && party.name !== "" ? (
                <div>
                  <p className="text-sm font-bold">{personLabel}</p>
                  <p className="text-sm">{party.name}</p>
                </div>
              ) : null;
            const companyRow =
              party.company_name != null && party.company_name !== "" ? (
                <div>
                  <p className="text-sm font-bold">Company name</p>
                  <p className="text-sm">{party.company_name}</p>
                </div>
              ) : null;
            return (
              <Card key={`${questionDef.id}-${party.id}`} className="gap-4">
                <CardHeader>
                  <CardTitle className="flex min-w-0 items-center gap-1 font-semibold text-xl">
                    <span className="min-w-0 break-words">
                      {questionDef.question}
                    </span>
                    <PartyRoleInfo questionText={questionDef.question} />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {useCoLabel ? (
                    <>
                      {companyRow}
                      {nameRow}
                    </>
                  ) : (
                    <>
                      {nameRow}
                      {companyRow}
                    </>
                  )}
                  {party.email != null && party.email !== "" && (
                    <div>
                      <p className="text-sm font-bold">Email</p>
                      <p className="text-sm">{party.email}</p>
                    </div>
                  )}
                  {party.phone != null && party.phone !== "" && (
                    <div>
                      <p className="text-sm font-bold">Phone</p>
                      <p className="text-sm">
                        {formatPhoneNumber(party.phone)}
                      </p>
                    </div>
                  )}
                  {(party.address1 != null && party.address1 !== "") ||
                  (party.city != null && party.city !== "") ? (
                    <div>
                      <p className="text-sm font-bold">Address</p>
                      <p className="text-sm">
                        {[party.address1, party.address2]
                          .filter(Boolean)
                          .join(", ")}
                        {(party.address1 || party.address2) &&
                          (party.city || party.state || party.zipcode) &&
                          " "}
                        {[party.city, party.state, party.zipcode]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}

          {/* Additional questions tile (read-only) */}
          {license && questionsData && (
            <Card className="gap-4">
              <CardHeader>
                <CardTitle className="font-semibold text-xl">
                  Additional Information
                </CardTitle>
              </CardHeader>
              <AdditionalQuestionsCardContent
                additionalQuestions={questionsData}
                data={license}
              />
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Perform Inspection</CardTitle>
              <CardDescription>
                Answer the following questions to complete the inspection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form.Subscribe selector={(state) => state.values}>
                {(formValues) => (
                  <>
                    {allQuestions.map((question) => {
                      // Skip information-only questions
                      if (question.answer_type === "information") {
                        return (
                          <div key={question.id} className="space-y-2">
                            <h3 className="font-medium text-sm">
                              {question.question}
                            </h3>
                            <div className="rounded-md bg-muted/50 border p-4">
                              <div
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: Content comes from trusted source
                                dangerouslySetInnerHTML={{
                                  __html: question.information_text ?? "",
                                }}
                              />
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={question.id} className="space-y-4">
                          <form.AppField
                            name={question.id}
                            validators={{
                              onChange: validatorForQuestionType(
                                question.answer_type,
                                question.answer_required ?? false,
                                isNacStyleAnswerType(question.answer_type)
                                  ? ((
                                      question as {
                                        select_from_fixed_list?: boolean;
                                      }
                                    ).select_from_fixed_list ?? false)
                                  : undefined,
                                isNacStyleAnswerType(question.answer_type)
                                  ? nacFieldRequirementsForValidation(question)
                                  : undefined,
                                question.allow_multiple_urls ?? false,
                                question.allow_multiple_documents ?? false,
                              ),
                            }}
                          >
                            {(field) => (
                              <FormFieldWrapper
                                field={field}
                                label={question.question}
                              >
                                <QuestionField
                                  answerType={question.answer_type}
                                  allowMultipleUrls={
                                    question.allow_multiple_urls ?? false
                                  }
                                  allowMultipleDocuments={
                                    question.allow_multiple_documents ?? false
                                  }
                                  field={field}
                                  questionId={question.id}
                                  confirmationText={question.information_text}
                                  selectFromFixedList={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? ((
                                          question as {
                                            select_from_fixed_list?: boolean;
                                          }
                                        ).select_from_fixed_list ?? false)
                                      : undefined
                                  }
                                  nacRequirements={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? nacFieldRequirementsForValidation(
                                          question,
                                        )
                                      : undefined
                                  }
                                  useContactLabel={
                                    question.answer_type ===
                                      "company_contact" &&
                                    Boolean(question.use_contact_label)
                                  }
                                  answerOptions={
                                    question.answer_type === "single_select" ||
                                    question.answer_type === "multi_select"
                                      ? question.answer_options
                                        ? answerOptionsToSelectOptions(
                                            question.answer_options,
                                            question.answer_option_keys_order,
                                          )
                                        : undefined
                                      : undefined
                                  }
                                  questionName={question.question}
                                  userType={userType}
                                  accountData={
                                    license?.registrant_account ?? undefined
                                  }
                                  currentQuestionBracketLabel={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? (extractBracketLabel(
                                          question.question,
                                        ) ?? undefined)
                                      : undefined
                                  }
                                  allowCopyRegistrantInfo={
                                    isNacStyleAnswerType(question.answer_type)
                                      ? ((
                                          question as {
                                            allow_copy_registrant_info?: boolean;
                                          }
                                        ).allow_copy_registrant_info ?? true)
                                      : undefined
                                  }
                                  {...companyCopyFieldProps({
                                    question,
                                    questions: allQuestions,
                                    answers: formValues as Record<
                                      string,
                                      unknown
                                    >,
                                    registrantAccountId:
                                      license?.registrant_account?.id,
                                    excludeLicenseId: license?.id,
                                  })}
                                />
                              </FormFieldWrapper>
                            )}
                          </form.AppField>

                          {/* Comment field for this question (skip for inspection_date) */}
                          {question.id !== "inspection_date" && (
                            <div className="space-y-2 pl-4 border-l-2 border-muted">
                              <Textarea
                                id={`comment-${question.id}`}
                                value={questionComments[question.id] ?? ""}
                                onChange={(e) => {
                                  setQuestionComments((prev) => ({
                                    ...prev,
                                    [question.id]: e.target.value,
                                  }));
                                }}
                                placeholder="Add a comment about this question..."
                                rows={2}
                              />
                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`share-${question.id}`}
                                  checked={
                                    questionShareFlags[question.id] ?? false
                                  }
                                  onChange={(e) => {
                                    setQuestionShareFlags((prev) => ({
                                      ...prev,
                                      [question.id]: e.target.checked,
                                    }));
                                  }}
                                  className="h-4 w-4"
                                />
                                <Label
                                  htmlFor={`share-${question.id}`}
                                  className="cursor-pointer text-sm"
                                >
                                  Share with registrant
                                </Label>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </form.Subscribe>

              {/* General Comment Section */}
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="shared-comment">Shared with registrant</Label>
                  <Textarea
                    id="shared-comment"
                    value={sharedComment}
                    onChange={(e) => setSharedComment(e.target.value)}
                    placeholder="Add a comment to share with the registrant..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="internal-comment">Internal Comment</Label>
                  <Textarea
                    id="internal-comment"
                    value={internalComment}
                    onChange={(e) => setInternalComment(e.target.value)}
                    placeholder="Add an internal comment (not shared with registrant)..."
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  onClick={async () => {
                    const errors = await form.validateAllFields("submit");
                    if (errors.length > 0) {
                      toast.error(
                        "Please fix validation errors before completing the inspection",
                        {
                          description: `Found ${errors.length} error(s)`,
                        },
                      );
                      return;
                    }
                    await handleCompleteInspection(false);
                  }}
                  disabled={
                    updateInspectionMutation.isPending ||
                    commentHandlers.createCommentMutation.isPending
                  }
                >
                  Inspection Failed
                </Button>
                <Button
                  onClick={async () => {
                    const errors = await form.validateAllFields("submit");
                    if (errors.length > 0) {
                      toast.error(
                        "Please fix validation errors before completing the inspection",
                        {
                          description: `Found ${errors.length} error(s)`,
                        },
                      );
                      return;
                    }
                    await handleCompleteInspection(true);
                  }}
                  disabled={
                    updateInspectionMutation.isPending ||
                    commentHandlers.createCommentMutation.isPending
                  }
                >
                  Inspection Passed
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Reassignment Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Inspection?</DialogTitle>
            <DialogDescription>
              This inspection is currently assigned to another user. Would you
              like to reassign it to yourself?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleReassign(false)}
              disabled={updateInspectionMutation.isPending}
            >
              No, Cancel
            </Button>
            <Button
              onClick={() => handleReassign(true)}
              disabled={updateInspectionMutation.isPending}
            >
              Yes, Reassign to Me
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
